"use client";

import { useState, useMemo, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Edit, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, X, User, Calendar, DollarSign, FileText, Upload, Loader2 } from "lucide-react";
// import moment from "moment";
import moment from "moment-timezone";
import EmployeeDetailsModal from "./EmployeeDetailsModal";
import { parseReceipts, isPdfReceipt } from "../utils/receiptUtils";
import { isDateTimeInBusinessRange, formatCstDisplayDateTime } from "../utils/cstDate";
import { useAuth } from "../context/AuthContext";
import { canPerformTransactionActions } from "../constants/sidebarTabs";

export const PettyCashTransactionTable = ({
  transactions,
  currentPage,
  setCurrentPage,
  entriesPerPage,
  setEntriesPerPage,
  onEdit,
  onDelete,
  onUploadReceipt,
  dateRange,
  employeeApiService,
  employees,
  currentUser,
}) => {
  const { currentUser: authUser } = useAuth();
  const effectiveUser = currentUser || authUser;
  const showActions = canPerformTransactionActions(effectiveUser, "petty_cash");
  // State for sorting and modals
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [modalEmployee, setModalEmployee] = useState(null);
  const [modalRecipient, setModalRecipient] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptPreviewList, setReceiptPreviewList] = useState([]);
  const [activeReceiptIndex, setActiveReceiptIndex] = useState(0);
  // State for employee details modal
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  const [employeeDetailsError, setEmployeeDetailsError] = useState(null);
  const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [uploadingTransactionId, setUploadingTransactionId] = useState(null);
  const uploadTargetRef = useRef(null);
  const fileInputRef = useRef(null);

  // Filter transactions by date range
  const filteredTransactions = useMemo(() => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return transactions;
    }

    return transactions.filter((transaction) =>
      isDateTimeInBusinessRange(transaction.date, dateRange.start, dateRange.end)
    );
  }, [transactions, dateRange]);

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    const sortableTransactions = [...filteredTransactions];

    if (sortConfig.key) {
      sortableTransactions.sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        // Handle date sorting
        if (sortConfig.key === 'date') {
          aValue = moment(aValue).valueOf();
          bValue = moment(bValue).valueOf();
        }

        // Handle numeric sorting for amounts
        if (sortConfig.key === 'transaction_amount' || sortConfig.key === 'before_balance' || sortConfig.key === 'after_balance') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableTransactions;
  }, [filteredTransactions, sortConfig]);

  // Calculate pagination values
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = sortedTransactions.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(sortedTransactions.length / entriesPerPage);

  // Handle employee name click (Added By)
  const handleEmployeeNameClick = (employeeName, e) => {
    e.stopPropagation();
    setModalEmployee(employeeName);
    setShowEmployeeModal(true);
  };

  const handleRecipientNameClick = async (recipientName, e) => {
    e.stopPropagation();

    // Find employee by name
    const employee = employees.find(emp => emp.name === recipientName);

    if (employee && employeeApiService) {
      // Show employee details modal
      setLoadingEmployeeDetails(true);
      setEmployeeDetailsError(null);
      setShowEmployeeDetailsModal(true);

      try {
        const startDate = dateRange?.start ? moment(dateRange.start).format('YYYY-MM-DD') : moment().subtract(30, 'days').format('YYYY-MM-DD');
        const endDate = dateRange?.end ? moment(dateRange.end).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

        const [completeDataResponse, performanceResponse] = await Promise.all([
          employeeApiService.getCompleteEmployeeData(employee.id, { startDate, endDate }),
          employeeApiService.getEmployeePerformanceMetrics(employee.id, { startDate, endDate })
        ]);
        if (completeDataResponse.success && performanceResponse.success) {
          setEmployeeDetails({
            employee: employee,
            completeData: completeDataResponse?.data?.body?.data,
            performanceMetrics: performanceResponse?.data?.body?.data
          });
        } else {
          setEmployeeDetailsError('Failed to load employee details');
        }
      } catch (error) {
        console.error('Error loading employee details:', error);
        setEmployeeDetailsError('Error loading employee details');
      } finally {
        setLoadingEmployeeDetails(false);
      }
    } else {
      // Fallback to original recipient modal
      setModalRecipient(recipientName);
      setShowEmployeeDetailsModal(true);
    }
  };

  const closeEmployeeDetailsModal = () => {
    setShowEmployeeDetailsModal(false);
    setEmployeeDetails(null);
    setEmployeeDetailsError(null);
    setShowEmployeeModal(false)
  };

  const closeRecipientModal = () => {
    setModalRecipient(null);
    setShowEmployeeModal(false)
  };


  // Request sort
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Format number with commas
  const formatNumberWithCommas = (amount) => {
    const numericAmount = parseFloat(amount);
    return numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format amount with sign and color
  const formatAmount = (amount, type) => {
    const sign = type === "deposit" ? "+" : "-";
    const color = type === "deposit" ? "text-green-600" : "text-red-600";
    return (
      <span className={`font-semibold ${color}`}>
        {sign}${formatNumberWithCommas(amount)}
      </span>
    );
  };

  const formatDate = (dateString) => formatCstDisplayDateTime(dateString);
  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="icon"
          className="h-8 w-8"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return buttons;
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === "ascending" ?
        <ChevronDown className="inline-block ml-1 h-4 w-4 rotate-180" /> :
        <ChevronDown className="inline-block ml-1 h-4 w-4" />;
    }
    return <ChevronDown className="inline-block ml-1 h-4 w-4 opacity-30" />;
  };

  // Get employee statistics (Added By)
  const getEmployeeStats = (employeeName) => {
    const employeeTransactions = filteredTransactions.filter(t => t.added_by === employeeName);
    const totalTransactions = employeeTransactions.length;
    const totalAmount = employeeTransactions.reduce((sum, t) => sum + Math.abs(parseFloat(t.transaction_amount || 0)), 0);
    const deposits = employeeTransactions.filter(t => t.transaction_type === 'deposit').length;
    const withdrawals = employeeTransactions.filter(t => t.transaction_type === 'withdrawal').length;
    const latestTransaction = employeeTransactions.length > 0 ?
      employeeTransactions.sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())[0] : null;

    return {
      totalTransactions,
      totalAmount,
      deposits,
      withdrawals,
      latestTransaction
    };
  };

  const handleViewReceipt = (receipts) => {
    const parsed = parseReceipts(receipts);

    if (parsed.length === 0) {
      toast.error("No receipt found for this transaction");
      return;
    }

    setReceiptPreviewList(parsed);
    setActiveReceiptIndex(0);
    setShowReceiptModal(true);
  };

  const closeReceiptModal = () => {
    setShowReceiptModal(false);
    setReceiptPreviewList([]);
    setActiveReceiptIndex(0);
  };

  const activeReceipt = receiptPreviewList[activeReceiptIndex] || null;

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "Tumblr");
    data.append("cloud_name", "dgqt5ockx");

    const response = await axios.post(
      "https://api.cloudinary.com/v1_1/dgqt5ockx/image/upload",
      data
    );

    return {
      url: response.data.secure_url,
      publicId: response.data.public_id,
      originalName: file.name,
      size: file.size,
      type: file.type,
      uploaded: true,
    };
  };

  const triggerReceiptUpload = (transaction, e) => {
    e.stopPropagation();
    if (!onUploadReceipt || uploadingTransactionId) return;
    uploadTargetRef.current = transaction;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleReceiptFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const transaction = uploadTargetRef.current;
    if (!transaction || files.length === 0) return;

    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 5MB.`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          `File ${file.name} is not a supported format. Please upload images or PDF files.`
        );
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploadingTransactionId(transaction.id);

    try {
      const uploadResults = [];
      for (const file of validFiles) {
        if (file.type.startsWith("image/")) {
          uploadResults.push(await uploadToCloudinary(file));
        } else {
          // PDFs: upload via Cloudinary as raw/auto when possible; keep metadata otherwise
          try {
            uploadResults.push(await uploadToCloudinary(file));
          } catch {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }

      if (uploadResults.length === 0) {
        toast.error("No files were uploaded");
        return;
      }

      const existingReceipts = parseReceipts(transaction.receipts);
      const success = await onUploadReceipt(transaction, [
        ...existingReceipts,
        ...uploadResults,
      ]);

      if (success) {
        toast.success("Receipt uploaded successfully!");
      }
    } catch (error) {
      console.error("Receipt upload error:", error);
      toast.error(error.message || "Failed to upload receipt");
    } finally {
      setUploadingTransactionId(null);
      uploadTargetRef.current = null;
      e.target.value = "";
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf"
        className="hidden"
        onChange={handleReceiptFileChange}
      />
      <div className="bg-white rounded-md shadow overflow-hidden">
        {/* Show date range info if applied */}
        {dateRange && dateRange.start && dateRange.end && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
            <p className="text-sm text-blue-800">
              <Calendar className="inline h-4 w-4 mr-1" />
              Showing transactions from {moment(dateRange.start).format("MM/DD/YYYY")} to {moment(dateRange.end).format("MM/DD/YYYY")}
              <span className="ml-2 text-blue-600">({filteredTransactions.length} total transactions)</span>
            </p>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => requestSort("date")}>
                Date
                {renderSortIcon("date")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("before_balance")}>
                Petty Cash Balance
                {renderSortIcon("before_balance")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("transaction_amount")}>
                Transaction
                {renderSortIcon("transaction_amount")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("notes")}>
                Transaction Details
                {renderSortIcon("notes")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("department")}>
                Department
                {renderSortIcon("department")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("name")}>
                Cash Given To
                {renderSortIcon("name")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("after_balance")}>
                After Balance
                {renderSortIcon("after_balance")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("added_by")}>
                Added By
                {renderSortIcon("added_by")}
              </TableHead>
              <TableHead>
                Receipt
              </TableHead>
              {showActions && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showActions ? 10 : 9} className="text-center py-8 text-gray-500">
                  {filteredTransactions.length === 0 ? "No transactions found for the selected date range" : "No transactions found"}
                </TableCell>
              </TableRow>
            ) : (
              currentEntries.map((transaction) => (
                <TableRow key={transaction.id} className="hover:bg-gray-50">
                  <TableCell>
                    {formatDate(transaction.date)}
                  </TableCell>
                  <TableCell>${formatNumberWithCommas(transaction.before_balance)}</TableCell>
                  <TableCell>
                    {formatAmount(transaction.transaction_amount, transaction.transaction_type)}
                  </TableCell>
                  <TableCell>{transaction.notes || '-'}</TableCell>
                  <TableCell>{transaction.department || '-'}</TableCell>
                  <TableCell>
                    <button
                      className="font-medium text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                      onClick={(e) => handleRecipientNameClick(transaction.name, e)}
                    >
                      {transaction.name || '-'}
                    </button>
                  </TableCell>
                  <TableCell>${formatNumberWithCommas(transaction.after_balance)}</TableCell>
                  <TableCell>
                    <button
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                      onClick={(e) => handleEmployeeNameClick(transaction.added_by, e)}
                    >
                      {transaction.added_by || '-'}
                    </button>
                  </TableCell>
                  <TableCell className="text-center">
                    {parseReceipts(transaction?.receipts).length > 0 ? (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center mx-auto text-gray-600 hover:text-blue-600"
                        title="View Receipt"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewReceipt(transaction.receipts);
                        }}
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  {showActions && (
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-600"
                          title="Upload Receipt"
                          disabled={uploadingTransactionId === transaction.id}
                          onClick={(e) => triggerReceiptUpload(transaction, e)}
                        >
                          {uploadingTransactionId === transaction.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(transaction);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(transaction);
                          }}
                          className={!transaction.isDeletable ? "opacity-50 cursor-not-allowed h-8 w-8 text-red-600" : "h-8 w-8 text-red-600"}
                          disabled={!transaction.isDeletable}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-4 text-sm px-4 py-2 border-t">
          <div>
            Showing {sortedTransactions.length > 0 ? indexOfFirstEntry + 1 : 0} to{" "}
            {Math.min(indexOfLastEntry, sortedTransactions.length)} of {sortedTransactions.length} entries
          </div>

          <div className="flex items-center gap-2">
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>entries per page</span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {renderPaginationButtons()}

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Employee Details Modal (Added By) */}
      {showEmployeeModal && modalEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{modalEmployee}</h2>
                  <p className="text-sm text-gray-500">
                    Employee Petty Cash Transaction Details
                    {dateRange && dateRange.start && dateRange.end && (
                      <span className="ml-2">
                        ({moment(dateRange.start).format("MM/DD/YYYY")} - {moment(dateRange.end).format("MM/DD/YYYY")})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeRecipientModal}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Transactions</p>
                      <p className="text-xl font-bold text-blue-800">
                        {getEmployeeStats(modalEmployee).totalTransactions}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Amount</p>
                      <p className="text-xl font-bold text-green-800">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).totalAmount)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <ChevronDown className="h-4 w-4 text-emerald-600 rotate-180" />
                    </div>
                    <div>
                      <p className="text-sm text-emerald-600 font-medium">Deposits</p>
                      <p className="text-xl font-bold text-emerald-800">
                        {getEmployeeStats(modalEmployee).deposits}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <ChevronDown className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-red-600 font-medium">Withdrawals</p>
                      <p className="text-xl font-bold text-red-800">
                        {getEmployeeStats(modalEmployee).withdrawals}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Latest Transaction Card */}
              {getEmployeeStats(modalEmployee).latestTransaction && (
                <div className="bg-gray-50 p-4 rounded-lg border mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Latest Transaction</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-medium">
                        {formatDate(getEmployeeStats(modalEmployee).latestTransaction.date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="font-medium">
                        {formatAmount(
                          getEmployeeStats(modalEmployee).latestTransaction.transaction_amount,
                          getEmployeeStats(modalEmployee).latestTransaction.transaction_type
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Cash Given To</p>
                      <p className="font-medium text-blue-600">
                        {getEmployeeStats(modalEmployee).latestTransaction.name || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Transactions Table */}
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800">Recent Transactions</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cash Given To</th>
                        {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax (12%)</th> */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredTransactions
                        .filter(t => t.added_by === modalEmployee)
                        .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
                        .slice(0, 10)
                        .map((transaction, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatDate(transaction.date)}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-blue-600">
                              {transaction.name || '-'}
                            </td>
                            {/* <td className="px-4 py-3 text-sm">
                                {transaction.tax >= 0 ? '+' : '-'}${Math.abs(transaction.tax).toFixed(2)}
                                  </td> */}
                            <td className="px-4 py-3 text-sm">
                              {formatAmount(transaction.transaction_amount, transaction.transaction_type)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {transaction.notes || '-'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button onClick={closeRecipientModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recipient Details Modal (Cash Given To) */}
      {/* Employee Details */}
      <EmployeeDetailsModal
        isOpen={showEmployeeDetailsModal}
        onClose={closeEmployeeDetailsModal}
        loading={loadingEmployeeDetails}
        error={employeeDetailsError}
        employeeDetails={employeeDetails}
        dateRange={dateRange}
        fallbackName={modalRecipient}
      />

      {showReceiptModal && activeReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Receipt{receiptPreviewList.length > 1 ? ` (${activeReceiptIndex + 1} of ${receiptPreviewList.length})` : ""}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeReceiptModal}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4">
              {isPdfReceipt(activeReceipt) ? (
                <iframe
                  src={activeReceipt.url}
                  title={activeReceipt.originalName || "Receipt PDF"}
                  className="w-full h-[70vh] rounded border"
                />
              ) : (
                <img
                  src={activeReceipt.url}
                  alt={activeReceipt.originalName || "Receipt"}
                  className="w-full h-auto max-h-[70vh] object-contain rounded"
                />
              )}
            </div>
            {receiptPreviewList.length > 1 && (
              <div className="flex items-center justify-between gap-3 p-4 border-t">
                <Button
                  variant="outline"
                  disabled={activeReceiptIndex === 0}
                  onClick={() => setActiveReceiptIndex((index) => Math.max(0, index - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  disabled={activeReceiptIndex >= receiptPreviewList.length - 1}
                  onClick={() =>
                    setActiveReceiptIndex((index) =>
                      Math.min(receiptPreviewList.length - 1, index + 1)
                    )
                  }
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};